import Utils, {createUUId, formatMoney, formatPhoneNumber, stringComparator} from "../utils/utils";
import BizPrint from "./types";
import ServerSideElementTypes = BizPrint.ServerSideElementTypes;

/**
 * Houses the logic for the printing service.
 */
class BizPrintService {

    static application = {
        version: 'V1',
        name: 'Operator Panel',
    };
    static pages = {
        productInformation: {
            name: 'Product Information',
            version: 'V1',
        },
        productLabelsSerialized: {
            name: 'Serialized Product Labels',
            version: 'V1',
        },
        productLabelsUnSerialized: {
            name: 'UnSerialized Product Labels',
            version: 'V1',
        },
        productsCatalogue: {
            name: 'Products Catalogue',
            version: 'V1',
        },
        productsBrochure: {
            name: 'Products Poster', // we use a different name than v1 to ensure that the older templates do not show anymore.
            version: 'V1',
        },
        packingLists: {
            name: 'Packing Lists',
            version: 'V1',
        },
        transactionReceipt: {
            name: 'Transaction Receipt',
            version: 'V1',
        },
    }

    /**
     * Generates the content for the page.
     *
     * 1. The page name is used to determine which page is being generated.
     * 2. The page data is used to generate the content for the page.
     * 3. The content is returned as a string.
     *
     * @param pageName The name of the page.
     * @param pageData The data to be used to generate the content.
     */
    static generatePageContent(pageName: string, pageData: Record<string, any>): string {
        let response;
        const version = Object.values(this.pages).find(e => e.name === pageName)?.version;
        switch (pageName) {
            case this.pages.productInformation.name:
                response = {
                    data: this.generateProductInformationContent(pageData as BizPrint.ProductInformationPageData),
                    version: version,
                    pageName: pageName,
                };
                break;
            case this.pages.productLabelsSerialized.name:
                response = {
                    data: this.generateProductLabelsContent(pageData as BizPrint.ProductLabelsPageData, true),
                    version: version,
                    pageName: pageName,
                };
                break
            case this.pages.productLabelsUnSerialized.name:
                response = {
                    data: this.generateProductLabelsContent(pageData as BizPrint.ProductLabelsPageData, false),
                    version: version,
                    pageName: pageName,
                };
                break
            case this.pages.productsCatalogue.name:
                response = {
                    data: this.generateProductsCatalogueContent(pageData as BizPrint.ProductsCataloguePageData),
                    version: version,
                    pageName: pageName,
                }
                break;
            case this.pages.productsBrochure.name:
                response = {
                    data: this.generateProductsBrochureContent(pageData as BizPrint.ProductsBrochurePageData),
                    version: version,
                    pageName: pageName,
                }
                break;
            case this.pages.packingLists.name:
                response = {
                    data: this.generatePackingListsContent(pageData as BizPrint.PackingListsPageData),
                    version: version,
                    pageName: pageName,
                }
                break;
            case this.pages.transactionReceipt.name:
                response = {
                    data: this.generateTransactionReceiptContent(pageData as BizPrint.TransactionReceiptPageData),
                    version: version,
                    pageName: pageName,
                }
                break;
            default:
                response = undefined;
                break;
        }
        return response ? JSON.stringify(response) : '';
    }

    /**
     * Generates the content for the product information page.
     *
     * @param pageData  The data to be used to generate the content.
     * @private
     */
    private static generateProductInformationContent(pageData: BizPrint.ProductInformationPageData): BizPrint.TemplateDataLayerResponseDs {
        const response: BizPrint.TemplateDataLayerResponseDs = [];
        if (!pageData)
            return response;

        response.push({
            title: 'Title',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'title',
            value: pageData.title ?? null,
        });

        response.push({
            title: 'Part No',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partNo',
            value: pageData.partNo ?? null,
        });

        response.push({
            title: 'UPC',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'upc',
            value: pageData.upc ?? null,
        });
        response.push({
            title: 'UPC Barcode',
            type: BizPrint.ServerSideElementTypes.barcode,
            name: 'upcBarcode',
            value: pageData.upc ?? null,
        });

        response.push({
            title: 'Cost',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'cost',
            value: pageData.cost?.toString() ?? null,
        });
        response.push({
            title: 'Avg Cost',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'avgCost',
            value: pageData.avgCost?.toString() ?? null,
        });

        response.push({
            title: 'Main Category',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'mainCategory',
            value: pageData.mainCategory ?? null,
        });

        response.push({
            title: 'Image',
            type: BizPrint.ServerSideElementTypes.image,
            name: 'image',
            value: pageData.image ?? null,
        });

        response.push({
            title: 'Description',
            type: BizPrint.ServerSideElementTypes.html,
            name: 'description',
            value: pageData.description ?? null,
        });

        return response;
    }

    /**
     * Generates the content for the serialized product labels page.
     *
     * @param pageData      The data to be used to generate the content.
     * @param serialized    Whether or not the product is serialized.
     * @private
     */
    private static generateProductLabelsContent(pageData: BizPrint.ProductLabelsPageData, serialized: boolean): BizPrint.TemplateDataLayerResponseDs {
        const response: BizPrint.TemplateDataLayerResponseDs = [];
        if (!pageData)
            return response;

        const productsAvery: BizPrint.TemplateElementAveryResponseDs = {
            title: serialized ? 'Serialized Products' : 'Un-serialized Products',
            type: BizPrint.ServerSideElementTypes.averyLabel,
            name: serialized ? 'serializedProducts' : 'Un-serializedProducts',
            value: [],
        }

        for (const product of pageData) {
            const productElementData: BizPrint.TemplateDataLayerResponseDs = [];
            productElementData.push({
                title: 'Part No',
                type: BizPrint.ServerSideElementTypes.text,
                name: 'partNo',
                value: product.partNo ?? null,
            });
            productElementData.push({
                title: 'UPC',
                type: BizPrint.ServerSideElementTypes.text,
                name: 'upc',
                value: product.upc ?? null,
            });
            productElementData.push({
                title: 'UPC Barcode',
                type: BizPrint.ServerSideElementTypes.barcode,
                name: 'upcBarcode',
                value: product.upc ?? null,
            });
            productElementData.push({
                title: 'Cost',
                type: BizPrint.ServerSideElementTypes.text,
                name: 'cost',
                value: product.cost?.toString() ?? null,
            });
            productElementData.push({
                title: 'Avg Cost',
                type: BizPrint.ServerSideElementTypes.text,
                name: 'avgCost',
                value: product.avgCost?.toString() ?? null,
            });
            productElementData.push({
                title: 'Main Category',
                type: BizPrint.ServerSideElementTypes.text,
                name: 'mainCategory',
                value: product.mainCategory ?? null,
            });
            productElementData.push({
                title: 'Image',
                type: BizPrint.ServerSideElementTypes.image,
                name: 'image',
                value: product.image ?? null,
            });
            productElementData.push({
                title: 'Description',
                type: BizPrint.ServerSideElementTypes.html,
                name: 'description',
                value: product.description ?? null,
            });
            if (serialized) {
                productElementData.push({
                    title: 'Serial No',
                    type: BizPrint.ServerSideElementTypes.text,
                    name: 'serialNo',
                    value: product.serialNo ?? null,
                });
                productElementData.push({
                    title: 'Serial No Barcode',
                    type: BizPrint.ServerSideElementTypes.barcode,
                    name: 'serialNoBarcode',
                    value: product.serialNo ?? null,
                });
            }
            productsAvery.value?.push(productElementData);
        }
        response.push(productsAvery);
        return response;
    }

    /**
     * Generates the content for the products catalogue page.
     * @param products      The data to be used to generate the content.
     * @private
     */
    private static generateProductsCatalogueContent(products: BizPrint.ProductsCataloguePageData): BizPrint.TemplateDataLayerResponseDs {
        const categories: Record<string, Record<string, typeof products>> = {};
        for (const product of products) {
            // requested by Sep @ April 4, 2024
            if (!product.notes?.length && !product.technicalProperties?.length && !product.coverImageFileName) {
                continue;
            }

            if (!product.makeTitle)
                product.makeTitle = '';
            if (!product.parentCategoryTitle)
                product.parentCategoryTitle = '';

            if (!categories[product.parentCategoryTitle])
                categories[product.parentCategoryTitle] = {};
            if (!categories[product.parentCategoryTitle][product.makeTitle])
                categories[product.parentCategoryTitle][product.makeTitle] = [];

            categories[product.parentCategoryTitle][product.makeTitle].push(product);
        }

        const makeIdentifier = createUUId();
        const pageData = {
            categories: Object.entries(categories).map(([categoryTitle, makes]) => ({
                    title: categoryTitle,
                    makes: Object.entries(makes).map(([makeTitle, products]) => ({
                        title: makeTitle,
                        products: products
                            .map(e => ({...e, tags: e.tags.length ? e.tags : [{name: '', iconFileName: ''}],}))
                            .sort((a, b) => stringComparator(a.partNo ?? '', b.partNo ?? "")),
                    })),
                    // TODO: code types --> fix this
                    AMCodes: Object.entries(makes).reduce((agg, make) => {
                        agg.push(make[0], makeIdentifier, '', ...make[1].map(e => [e.amCode ?? '', e.partNo ?? '']).flat() as Array<string>);
                        return agg;
                    }, [] as Array<string>),
                    OECodes: Object.entries(makes).reduce((agg, make) => {
                        agg.push(make[0], makeIdentifier, '', ...make[1].map(e => [e.oeCode ?? '', e.partNo ?? '']).flat() as Array<string>);
                        return agg;
                    }, [] as Array<string>),
                    colorCode: Object.values(makes)[0][0].category.colorCode,
                })
            ),
        };

        const response: BizPrint.TemplateDataLayerResponseDs = [];

        const productCategoriesGrid: BizPrint.TemplateElementGridResponseDs = {
            title: "Product Categories Grid",
            type: BizPrint.ServerSideElementTypes.grid,
            name: "productCategoriesGrid",
            value: [],
        };
        const amCodeCategoriesGrid: BizPrint.TemplateElementGridResponseDs = {
            title: "AM Code Categories Grid",
            type: BizPrint.ServerSideElementTypes.grid,
            name: "amCodesCategoriesGrid",
            value: [],
        };

        const oeCodeCategoriesGrid: BizPrint.TemplateElementGridResponseDs = {
            title: "OE Code Categories Grid",
            type: BizPrint.ServerSideElementTypes.grid,
            name: "oeCodesCategoriesGrid",
            value: [],
        };

        for (const category of pageData.categories) {
            const categoryTitle: BizPrint.TemplateElementResponseDs = {
                title: 'Category Title',
                type: BizPrint.ServerSideElementTypes.text,
                name: `categoryTitle`,
                value: category.title ?? null,
            }
            const categoryTitleBG: BizPrint.TemplateElementResponseDs = {
                title: 'Category Title BG',
                type: BizPrint.ServerSideElementTypes.container,
                name: `categoryTitleBG`,
                value: null,
                properties: [{
                    name: 'backgroundColor',
                    value: category.colorCode,
                }],
            }

            const productsMakesGrid: BizPrint.TemplateElementGridResponseDs = {
                title: 'Makes Grid',
                type: BizPrint.ServerSideElementTypes.grid,
                name: `productsMakesGrid`,
                value: [],
            }

            for (const make of category.makes) {

                const productsGrid: BizPrint.TemplateElementGridResponseDs = {
                    title: 'Products Grid',
                    type: BizPrint.ServerSideElementTypes.grid,
                    name: `productsGrid`,
                    value: [],
                }

                for (const product of make.products) {

                    const productList: Array<BizPrint.TemplateElementResponseDs> = [];

                    productList.push({
                        title: 'Part Number',
                        type: BizPrint.ServerSideElementTypes.text,
                        name: `productPartNumber`,
                        value: product.partNo ?? null,
                    });
                    productList.push({
                        title: 'Image',
                        type: BizPrint.ServerSideElementTypes.image,
                        name: `productImage`,
                        value: product.coverImageFileName ?? null,
                    });
                    productList.push({
                        title: 'Notes',
                        type: BizPrint.ServerSideElementTypes.html,
                        name: `productNotes`,
                        value: `<div>
                                    ${product.notes.length ? '<h2 style="font-weight: bold">Notes:</h2>' : ''}
                                    <ul style="padding-inline-start: 10px">
                                        ${product.notes.slice(0, 2).map(e => `<li>${e}</li>`).join('')}
                                    </ul>
                                </div>`,
                    });
                    productList.push({
                        title: 'Technical Info',
                        type: BizPrint.ServerSideElementTypes.html,
                        name: `productTechnicalInfo`,
                        value: `<div>
                                    ${product.technicalProperties.length ? '<h2 style="font-weight: bold">Technical Info:</h2>' : ''}
                                    ${product.technicalProperties
                            .map(e => `<span style="font-weight: bold">${e.title}</span>: ${e.value}`)
                            .join('<br/>')}
                                </div>`,
                    });
                    productList.push({
                        title: 'Parent Category',
                        type: BizPrint.ServerSideElementTypes.text,
                        name: `productParentCategory`,
                        value: product.parentCategoryTitle ?? null,
                    });
                    productList.push({
                        title: 'Product Make',
                        type: BizPrint.ServerSideElementTypes.text,
                        name: `productMake`,
                        value: product.makeTitle ?? null,
                    });

                    const tagsList: Array<BizPrint.TemplateDataLayerResponseDs> = [];
                    for (const tag of product.tags) {
                        const tags: Array<BizPrint.TemplateElementResponseDs> = [];
                        tags.push({
                            title: 'Tag Title',
                            type: BizPrint.ServerSideElementTypes.text,
                            name: `tagTitle`,
                            value: tag.name ?? null,
                        })
                        tags.push({
                            title: 'Tag Image',
                            type: BizPrint.ServerSideElementTypes.image,
                            name: `tagImage`,
                            value: tag.iconFileName ?? null,
                        })
                        tagsList.push(tags);
                    }

                    productList.push({
                        title: 'Tags',
                        type: BizPrint.ServerSideElementTypes.grid,
                        name: `productTags`,
                        value: tagsList,
                    });

                    productsGrid.value?.push(productList);

                }

                productsMakesGrid.value?.push([productsGrid]);
            }

            productCategoriesGrid.value?.push([categoryTitle, categoryTitleBG, productsMakesGrid]);

            const amCodesGrid: BizPrint.TemplateElementGridResponseDs = {
                title: 'AM Codes Grid',
                type: BizPrint.ServerSideElementTypes.grid,
                name: `amCodesGrid`,
                value: [
                    [
                        {
                            title: 'Product AM Codes Grid',
                            type: BizPrint.ServerSideElementTypes.grid,
                            name: `productAMCodesGrid`,
                            value: [
                                ...category.AMCodes.map<BizPrint.TemplateDataLayerResponseDs>((e, index, array) => [{
                                    title: 'Code Title',
                                    type: BizPrint.ServerSideElementTypes.text,
                                    name: `amCodeTitle`,
                                    properties: [{
                                        name: 'weight',
                                        value: array.length - 1 !== index && array[index + 1] === makeIdentifier
                                            ? 700
                                            : 400,

                                    }],
                                    value: e ?? null,
                                }]),
                                ...(!category.AMCodes.length ? [
                                    [{
                                        title: 'Code Title',
                                        type: BizPrint.ServerSideElementTypes.text,
                                        name: `amCodeTitle`,
                                        value: null,
                                    }]
                                ] : [])
                            ].filter(e => !!e.filter(e => e.value !== makeIdentifier).length),
                        }
                    ]
                ],
            }

            const oeCodesGrid: BizPrint.TemplateElementGridResponseDs = {
                title: 'OE Codes Grid',
                type: BizPrint.ServerSideElementTypes.grid,
                name: `oeCodesGrid`,
                value: [
                    [
                        {
                            title: 'Product OE Codes Grid',
                            type: BizPrint.ServerSideElementTypes.grid,
                            name: `productOECodesGrid`,
                            value: [
                                ...category.OECodes.map<BizPrint.TemplateDataLayerResponseDs>((e, index, array) => [{
                                    title: 'Code Title',
                                    type: BizPrint.ServerSideElementTypes.text,
                                    name: `oeCodeTitle`,
                                    properties: [{
                                        name: 'weight',
                                        value: array.length - 1 !== index && array[index + 1] === makeIdentifier
                                            ? 700
                                            : 400,
                                    }],
                                    value: e ?? null,
                                }]),
                                ...(!category.OECodes.length ? [
                                    [{
                                        title: 'Code Title',
                                        type: BizPrint.ServerSideElementTypes.text,
                                        name: `oeCodeTitle`,
                                        value: null,
                                    }]
                                ] : [])
                            ].filter(e => !!e.filter(e => e.value !== makeIdentifier).length),
                        }
                    ]
                ],
            }

            amCodeCategoriesGrid.value?.push([categoryTitle, categoryTitleBG, amCodesGrid]);
            oeCodeCategoriesGrid.value?.push([categoryTitle, categoryTitleBG, oeCodesGrid]);
        }

        response.push(productCategoriesGrid, amCodeCategoriesGrid, oeCodeCategoriesGrid);

        return response;
    }

    /**
     * Generates the content for the products catalogue page.
     * @param data      The data to be used to generate the content.
     * @private
     */
    private static generateProductsBrochureContent(data: BizPrint.ProductsBrochurePageData): BizPrint.TemplateDataLayerResponseDs {
        const pageData = {
            title: data.title,
            groups: data.groups.map((group) => ({
                ...group,
                title: group.title ?? '',
                products: group.products?.filter(product => !!product.coverImageFileName) ?? [],
            })),
            dividerColorConfig: data.dividerColorConfig,
        } satisfies BizPrint.ProductsBrochurePageData;

        const response: BizPrint.TemplateDataLayerResponseDs = [];

        const title: BizPrint.TemplateElementResponseDs = {
            title: 'Title',
            type: BizPrint.ServerSideElementTypes.text,
            name: `title`,
            value: pageData.title ?? "",
        }

        const productsGrid: BizPrint.TemplateElementGridResponseDs = {
            title: 'Products Grid',
            type: BizPrint.ServerSideElementTypes.grid,
            name: `productsGrid`,
            value: [],
        }

        const columnFormatter: BizPrint.TemplateIdentifierElementResponseDs = {
            title: 'Poster Column Formatter',
            type: BizPrint.ServerSideElementTypes.identifier,
            name: 'identifier-column',
            value: "OPERATOR_PLATFORM_BROCHURE_COLUMN_FORMATTER",
        }
        const rowFormatter: BizPrint.TemplateIdentifierElementResponseDs = {
            title: 'Poster Row Formatter',
            type: BizPrint.ServerSideElementTypes.identifier,
            name: 'identifier-row',
            value: "OPERATOR_PLATFORM_BROCHURE_ROW_FORMATTER",
        }

        for (const group of pageData.groups) {
            for (const product of group.products) {
                const productList: Array<BizPrint.TemplateElementResponseDs> = [];

                productList.push({
                    title: 'Part No',
                    type: BizPrint.ServerSideElementTypes.text,
                    name: `productPartNumber`,
                    value: product.partNo ?? null,
                });
                productList.push({
                    title: 'Image',
                    type: BizPrint.ServerSideElementTypes.image,
                    name: `productImage`,
                    value: product.coverImageFileName ?? null,
                });
                productList.push({
                    title: 'Group',
                    type: BizPrint.ServerSideElementTypes.text,
                    name: `productGroup`,
                    value: group.title ?? null,
                    properties: [{
                        name: 'color',
                        value: group.contrastColorCode,
                    }],
                });
                productList.push({
                    title: 'Group Background Color',
                    type: BizPrint.ServerSideElementTypes.container,
                    name: `groupBackgroundColor`,
                    value: null,
                    properties: [{
                        name: 'backgroundColor',
                        value: group.colorCode,
                    }],
                })
                productList.push({
                    title: 'Group Colored Line',
                    type: BizPrint.ServerSideElementTypes.container,
                    name: `groupLineColor`,
                    value: null,
                    properties: [{
                        name: 'backgroundColor',
                        value: pageData.dividerColorConfig.useGroupColor ? group.colorCode : pageData.dividerColorConfig.colorCode,
                    }],
                })
                productList.push({
                    title: 'Custom Note',
                    type: BizPrint.ServerSideElementTypes.text,
                    name: `productCustomNote`,
                    value: product.note ?? null,
                });

                productsGrid.value?.push(productList);
            }
        }

        response.push(productsGrid, columnFormatter, rowFormatter, title);

        return response;
    }

    /**
     * Generates the content for the transaction receipt page.
     * @param pageData  The data to be used to generate the content.
     * @private
     */
    private static generateTransactionReceiptContent(pageData: BizPrint.TransactionReceiptPageData): BizPrint.TemplateDataLayerResponseDs {
        const response: BizPrint.TemplateDataLayerResponseDs = [];
        response.push({
            title: 'Customer Name',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'customerName',
            value: pageData.customerName ?? null,
        });
        response.push({
            title: 'Customer Address',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'customerAddress',
            value: pageData.customerAddress ?? null,
        });
        response.push({
            title: 'Business Name',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'businessName',
            value: pageData.businessName ?? null,
        });
        response.push({
            title: 'Business Address',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'businessAddress',
            value: pageData.businessAddress ?? null,
        });
        response.push({
            title: 'Business Phone',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'businessPhone',
            value: pageData.businessPhone ? formatPhoneNumber(pageData.businessPhone) : null,
        });
        response.push({
            title: 'Business Email',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'businessEmail',
            value: pageData.businessEmail ?? null,
        });
        response.push({
            title: 'Business Website',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'businessWebsite',
            value: pageData.businessWebsite ?? null,
        });
        response.push({
            title: 'Auth Code',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'authCode',
            value: pageData.transactionAuthCode ?? null,
        });
        response.push({
            title: 'Transaction Title',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'transactionTitle',
            value: pageData.transactionTitle ?? null,
        });
        response.push({
            title: 'Transaction Date',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'transactionDate',
            value: pageData.transactionDate ?? null,
        });
        response.push({
            title: 'Total Amount',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'amount',
            value: (pageData.transactionTotalAmount === null || pageData.transactionTotalAmount === undefined)
                ? null
                : (formatMoney(pageData.transactionTotalAmount) ?? ''),
        });
        response.push({
            title: 'Transaction Type',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'transactionType',
            value: pageData.transactionType ?? null,
        });
        response.push({
            title: 'Reference Number',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'transactionReferenceNumber',
            value: pageData.transactionReferenceNumber ?? null,
        });
        response.push({
            title: 'Item Description',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'transactionItemDescription',
            value: pageData.transactionItemDescription ?? null,
        });
        response.push({
            title: 'Item Amount',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'transactionItemAmount',
            value: (pageData.transactionItemAmount === null || pageData.transactionItemAmount === undefined)
                ? null
                : (formatMoney(pageData.transactionItemAmount) ?? ''),
        });

        response.push({
            title: 'Card Type',
            type: ServerSideElementTypes.text,
            name: 'cardType',
            value: pageData.cardType ?? null,
        })
        response.push({
            title: 'Card Number',
            type: ServerSideElementTypes.text,
            name: 'cardNumber',
            value: pageData.cardLast4Digits ?? null,
        })
        response.push({
            title: 'Card Expiry',
            type: ServerSideElementTypes.text,
            name: 'cardExpiry',
            value: `${pageData.cardExpiry}/**`,
        })
        response.push({
            title: 'Status',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'status',
            value: pageData.status ?? null,
        })
        return response;
    }

    /**
     * Generates the content for the packing lists page.
     * @param pageData    The data to be used to generate the content.
     * @private
     */
    private static generatePackingListsContent(pageData: BizPrint.PackingListsPageData): BizPrint.TemplateDataLayerResponseDs {
        const response: BizPrint.TemplateDataLayerResponseDs = [];

        const packingListsInfoGrid: BizPrint.TemplateElementGridResponseDs = {
            title: "Packing Lists Grid",
            type: BizPrint.ServerSideElementTypes.grid,
            name: "packingListsGrid",
            value: [],
        };

        const customerInfo: BizPrint.TemplateDataLayerResponseDs = [
            {
                title: 'Customer Name',
                type: BizPrint.ServerSideElementTypes.text,
                name: 'customerName',
                value: pageData.customer.name ?? null,
            },
            {
                title: 'Customer Address',
                type: BizPrint.ServerSideElementTypes.text,
                name: 'customerAddress',
                value: `${pageData.customer.address ?? ''} ${pageData.customer.city ?? ''} ${pageData.customer.province ?? ''} ${pageData.customer.country ?? ''} ${pageData.customer.postalCode ?? ''}`,
            },
        ]

        const summaryInfo: {
            map: Record<string, number>,
            total: number
        } = {
            map: {},
            total: 0,
        }

        for (const packingList of pageData.packingLists) {

            const packingListInfo: BizPrint.TemplateDataLayerResponseDs = [
                {
                    title: 'Packing List SKID Number',
                    type: BizPrint.ServerSideElementTypes.text,
                    name: 'packingListSKIDNumber',
                    value: `${packingList.skidNumber?.toString() ?? 0} of ${packingList.data?.length?.toString() ?? 0}`,
                },
                {
                    title: 'Packing List Dimension',
                    type: BizPrint.ServerSideElementTypes.text,
                    name: 'packingListDimension',
                    value: packingList.dimension ?? null,
                },
                {
                    title: 'Packing List Weight',
                    type: BizPrint.ServerSideElementTypes.text,
                    name: 'packingListWeight',
                    value: packingList.weight?.toString() ?? null,
                },
                {
                    title: 'Packing List Ref No',
                    type: BizPrint.ServerSideElementTypes.text,
                    name: 'packingListRefNo',
                    value: packingList.refNo ?? null,
                },
                {
                    title: 'Packing List Total Pieces',
                    type: BizPrint.ServerSideElementTypes.text,
                    name: 'packingListTotalPieces',
                    value: `${packingList.data?.length?.toString() ?? 0} pc${packingList.data?.length === 1 ? '' : 's'}`,
                }
            ];

            const packingListItemsGrid: BizPrint.TemplateElementGridResponseDs = {
                title: 'Packing List Items Grid',
                type: BizPrint.ServerSideElementTypes.grid,
                name: `packingListItemsGrid`,
                value: [],
            }


            for (const item of packingList.data) {
                if (!summaryInfo.map[item.partNo])
                    summaryInfo.map[item.partNo] = 0;
                summaryInfo.map[item.partNo] += 1;
                summaryInfo.total += 1;

                packingListItemsGrid.value!.push(
                    [
                        {
                            title: 'Packing List Item',
                            type: BizPrint.ServerSideElementTypes.text,
                            name: 'packingListItem',
                            value: item.partNo ?? null,
                        }
                    ],
                    [
                        {
                            title: 'Packing List Item',
                            type: BizPrint.ServerSideElementTypes.text,
                            name: 'packingListItem',
                            value: item.serialNo ?? null,
                        },
                    ]
                );
            }

            packingListsInfoGrid.value!.push([...customerInfo, ...packingListInfo, packingListItemsGrid]);
        }

        response.push(packingListsInfoGrid);

        return response;
    }

    /**
     * Generates the content for the application.
     *
     * 1. The application data is used to generate the content for the application.
     * 2. The content is returned as a string.
     * @param applicationData   The data to be used to generate the content.
     */
    static generateApplicationContent(applicationData: BizPrint.ApplicationData): string {
        const response = {
            version: this.application.version,
            applicationName: this.application.name,
            data: [] as BizPrint.TemplateDataLayerResponseDs,
        };

        // Partner Info
        response.data.push({
            title: 'Partner Name',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerName',
            value: applicationData.partnerInfo.name ?? null,
        });
        response.data.push({
            title: 'Partner Address',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerAddress',
            value: applicationData.partnerInfo.fullAddress ?? null,
        });
        response.data.push({
            title: 'Partner Phone',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerPhone',
            value: applicationData.partnerInfo.phone ? formatPhoneNumber(applicationData.partnerInfo.phone) : null
        });
        response.data.push({
            title: 'Partner Fax',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerFax',
            value: applicationData.partnerInfo.fax ?? null,
        });
        response.data.push({
            title: 'Partner Logo',
            type: BizPrint.ServerSideElementTypes.image,
            name: 'partnerLogo',
            value: applicationData.partnerInfo.logo ?? null,
        });
        response.data.push({
            title: 'Partner Website',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerWebsite',
            value: Utils.formatLinkUrlDisplay(applicationData.partnerInfo.website) ?? null,
        });
        response.data.push({
            title: 'Partner City',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerCity',
            value: applicationData.partnerInfo.city ?? null,
        });
        response.data.push({
            title: 'Partner Country',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerCountry',
            value: applicationData.partnerInfo.country ?? null,
        });
        response.data.push({
            title: 'Partner Province',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerProvince',
            value: applicationData.partnerInfo.province ?? null,
        });
        response.data.push({
            title: 'Partner Postal/Zip Code',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerPostalCode',
            value: applicationData.partnerInfo.postalCode ?? null,
        });
        response.data.push({
            title: 'Partner Full Address',
            type: BizPrint.ServerSideElementTypes.text,
            name: 'partnerFullAddress',
            value: `${applicationData.partnerInfo.fullAddress ?? ''} ${applicationData.partnerInfo.city ?? ''} ${applicationData.partnerInfo.province ?? ''} ${applicationData.partnerInfo.postalCode ?? ''} ${applicationData.partnerInfo.country ?? ''}`,

        })

        return JSON.stringify(response);
    }
}

export default BizPrintService;
